import { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';

import supportingDocumentTypeService from '../../service/supportingDocumentType.service';
import { ButtonUI, CheckboxUI, DialogUI } from '../Interface';

const SupportingDocumentAssignmentDialog = ({
  open,
  onClose,
  itemCode,
  supportingDocumentAssignment,
  onChange,
  disabled,
  batchManaged,
  loadAllSupportingDocumentTypes,
}) => {
  const [supportingDocumentTypes, setSupportingDocumentTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !itemCode) return;

    setIsLoading(true);
    Promise.all([
      supportingDocumentTypeService.getItemLinks(itemCode),
      loadAllSupportingDocumentTypes ? supportingDocumentTypeService.getItems() : Promise.resolve(null),
    ])
      .then(([data, types]) => {
        const itemLinks = Array.isArray(data) ? data : [];
        const documentTypes = loadAllSupportingDocumentTypes
          ? Array.isArray(types)
            ? types
            : types?.rows ?? []
          : itemLinks.map((link) => link.supportingDocumentType).filter(Boolean);
        const documentTypeIds = new Set(documentTypes.map((type) => Number(type.id)));

        setSupportingDocumentTypes(documentTypes);
        setSelectedIds(
          (supportingDocumentAssignment ?? [])
            .filter(
              (assignment) =>
                assignment.itemCode === itemCode &&
                documentTypeIds.has(Number(assignment.supportingDocumentTypeId))
            )
            .map((assignment) => Number(assignment.supportingDocumentTypeId))
        );
      })
      .finally(() => setIsLoading(false));
  }, [itemCode, loadAllSupportingDocumentTypes, open, supportingDocumentAssignment]);

  const handleChange = (id, checked) => {
    setSelectedIds((currentIds) =>
      checked ? [...new Set([...currentIds, Number(id)])] : currentIds.filter((currentId) => currentId !== Number(id))
    );
  };

  const handleSave = () => {
    onChange(itemCode, selectedIds);
    onClose();
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent="Kísérő dokumentumok" fullWidth maxWidth="sm">
      <Box minHeight="8rem" mt="1rem">
        <Typography mb="1rem">
          Cikk: <strong>{itemCode}</strong>
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py="2rem">
            <CircularProgress size={32} />
          </Box>
        ) : supportingDocumentTypes.length > 0 ? (
          supportingDocumentTypes.map((type) => {
            const selected = selectedIds.includes(Number(type.id));
            const externalDisabled = !type.generated && !batchManaged && !selected;

            return (
              <Tooltip
                key={type.id}
                title={externalDisabled ? 'Külső típus csak sarzskezelt cikkhez választható.' : ''}
              >
                <Box display="flex" alignItems="center">
                  <CheckboxUI
                    sx={{ margin: 0, padding: '0.35rem' }}
                    checked={selected}
                    disabled={disabled || externalDisabled}
                    onChange={(event) => handleChange(type.id, event.target.checked)}
                  />
                  <Typography color={externalDisabled ? 'text.secondary' : 'text.primary'}>
                    {type.name} – {type.generated ? 'Generált' : 'Külső'}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <Typography>A cikkhez nincs kísérő dokumentumtípus rendelve.</Typography>
        )}
      </Box>

      <Box display="flex" gap="0.5rem" justifyContent="center" mt="1rem">
        {!disabled && (
          <ButtonUI text="Rögzítés" className="bg-success" onClick={handleSave} disabled={isLoading} />
        )}
        <ButtonUI text={disabled ? 'Bezárás' : 'Mégse'} className="bg-primary" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default SupportingDocumentAssignmentDialog;
